<template>
  <div>
    <p class="intro">
      Browse the library by adjusting the filters in the left-hand sidebar or by entering keywords in the search box.
      The Council is in the process of migrating documents from its archive website to the 
      <a href="/">current website</a>. If you're looking for older documents and can't find them in the library or on 
      the <a href="/council-meetings">Council Meetings</a> webpage, please contact the 
      <a href="/about/contact">Council office</a>.
    </p>

    <div id="search-form" class="library">
      <div class="filter">
        <!-- Keywords -->
        <div id="keyword">
          <h4>Keyword</h4>
          <div class="d-flex">
            <input
              class="form-group__keyword"
              name="keywords"
              placeholder="Enter keyword(s)"
              v-model="tmpKeywords"
              @keyup.enter="searchKeywords"
            />
            <button class="btn btn-keyword" @click="searchKeywords">
              <i class="fa fa-search"></i>
            </button>
          </div>
        </div>

        <!-- Dates -->
        <div class="dates">
          <h4>Document Date</h4>
          <div class="pb-2">
            <label>From</label>
            <input
              class="datepicker"
              id="start_date"
              placeholder="mm/dd/yyyy"
              v-model="filters.start_date"
            />
          </div>

          <div class="">
            <label>Until</label>
            <input
              class="datepicker"
              id="end_date"
              placeholder="mm/dd/yyyy"
              v-model="filters.end_date"
            />
          </div>
        </div>

        <!-- Management Plans -->
        <div class="checkboxes group plans">
          <h4>Management Plans</h4>
          <div v-for="plan in managementPlans" :key="plan.id" class="checkbox">
            <input
              :id="plan.title | titleToSlug"
              type="checkbox"
              :value="plan.id"
              v-model="filters.related"
            />
            <label :for="plan.title | titleToSlug">
              {{ plan.title }}
              <span v-if="plan.nickname"> ({{ plan.nickname }}) </span>
            </label>
          </div>
        </div>

        <!-- Document Types -->
        <div class="checkboxes group document_types">
          <h4>Document Type</h4>
          <div v-for="doc in documentTypes" :key="doc.label" class="checkbox">
            <input
              :id="doc.label | titleToSlug"
              type="checkbox"
              :value="doc.label"
              v-model="filters.types"
            />
            <label :for="doc.label | titleToSlug">
              {{ doc.label }}
              <span v-if="doc.nickname">
                {{ doc.nickname }}
              </span>
            </label>
          </div>
        </div>

        <!-- Committees -->
        <div class="checkboxes group committees">
          <h4>Committee</h4>
          <div
            v-for="committee in committees"
            :key="committee.id"
            class="checkbox"
          >
            <input
              :id="committee.title | titleToSlug"
              type="checkbox"
              :value="committee.id"
              v-model="filters.related"
            />
            <label :for="committee.title | titleToSlug">
              {{ committee.title }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <results
      :items.sync="results"
      :keywords="keywords"
      :filters="allSelectedFiltersLabeled"
      :loading="isLoading"
      :start-date="filters.start_date"
      :end-date="filters.end_date"
      @navigate="goToPage"
      @clearKeywords="clearKeywords"
      @clearFilter="clearFilter"
      @clearDate="clearDate"
      @clearAllFilters="clearAllFilters"
    />
  </div>
</template>

<script>
import Results from './Results.vue'
const axios = require('axios').default

export default {
  components: {
    Results,
  },

  props: {
    managementPlans: {
      type: Array,
      default: () => [],
    },
    documentTypes: {
      type: Array,
      default: () => [],
    },
    committees: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    isLoading: false,
    keywords: null,
    tmpKeywords: null,
    filters: {
      start_date: null,
      end_date: null,
      related: [],
      types: [],
    },
    currentPage: 1,
    results: null,
  }),

  computed: {
    allFiltersCombined() {
      const filters = []
      const keys = ['managementPlans', 'documentTypes', 'committees']

      keys.forEach((key) => {
        if (this[key]) {
          filters.push(
            ...this[key].map((item) => ({
              id: item.id,
              title: item.title || item.label,
            }))
          )
        }
      })

      return filters
    },

    allSelectedFiltersLabeled() {
      const related = this.filters.related.map((f) => {
        return this.allFiltersCombined.find((all) => all.id === Number(f))
      })

      const types = this.filters.types.map((f) => {
        return this.allFiltersCombined.find((all) => all.title === f)
      })

      return [...related, ...types]
    },
  },

  mounted() {
    this.applyData()
  },

  methods: {
    applyData() {
      this.instanciateDatepicker()
      const currentParams = new URLSearchParams(window.location.search)

      if (currentParams.get('keywords'))
        this.keywords = currentParams.get('keywords')
      if (currentParams.get('start_date'))
        this.filters.start_date = currentParams.get('start_date')
      if (currentParams.get('end_date'))
        this.filters.end_date = currentParams.get('end_date')
      if (currentParams.get('related'))
        this.filters.related = currentParams
          .get('related')
          .split(',')
          .map((r) => Number(r))
      if (currentParams.get('types'))
        this.filters.types = currentParams.get('types').split(',')

      this.search()
    },

    async searchKeywords() {
      this.keywords = this.tmpKeywords
      await this.search()
    },

    async search() {
      this.isLoading = true
      const url = this.buildUrl()
      await axios
        .get(url)
        .then((res) => {
          this.results = res.data
        })
        .catch((err) => {
          console.error(err)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    buildUrl() {
      const url = '/librarySearch.json'
      const query = {
        page: this.currentPage,
      }

      if (this.keywords) query.keywords = this.keywords
      if (this.filters.start_date) query.start_date = this.filters.start_date
      if (this.filters.end_date) query.end_date = this.filters.end_date
      if (this.filters.related.length > 0) query.related = this.filters.related
      if (this.filters.types.length > 0) query.types = this.filters.types

      const parameters = new URLSearchParams(query).toString()

      this.updateUrlSearchQuery(query)

      return [url, parameters].join('?')
    },

    goToPage(page) {
      this.currentPage = page
      this.search()
    },

    clearKeywords() {
      this.tmpKeywords = null
      this.keywords = null
    },

    clearFilter(removeFilter) {
      if (Number.isInteger(removeFilter)) {
        this.filters.related = this.filters.related.filter(
          (f) => f !== removeFilter
        )
      } else {
        this.filters.types = this.filters.types.filter(
          (f) => f !== removeFilter
        )
      }
    },

    clearAllFilters() {
      this.filters.related = []
      this.filters.types = []
      this.filters.start_date = null
      this.filters.end_date = null
    },

    clearDate(dateKey) {
      this.filters[dateKey] = null
    },

    instanciateDatepicker() {
      $('input#start_date').datepicker({
        onSelect: (selectedDate, datePicker) => {
          this.filters.start_date = selectedDate
        },
      })
      $('input#end_date').datepicker({
        onSelect: (selectedDate, datePicker) => {
          this.filters.end_date = selectedDate
        },
      })
    },

    updateUrlSearchQuery(query) {
      const params = new URLSearchParams(window.location.search)
      const url = new URL(window.location)

      params.forEach((p, key) => {
        if (!query[key]) {
          url.searchParams.delete(key)
        }
      })

      Object.keys(query).forEach((key) => url.searchParams.set(key, query[key]))

      window.history.pushState({}, '', url)
    },
  },

  watch: {
    filters: {
      handler(val) {
        this.search()
      },
      deep: true,
    },
    keywords(val) {
      if (!val) {
        this.tmpKeywords = val
        this.search()
      }
    },
  },
}
</script>

<style>
.pb-2 {
  padding-bottom: 2rem !important;
}

.me-1 {
  margin-right: 1rem !important;
}

.library .filter {
  margin-top: -19px;
}

.library .filter h4 {
  max-width: 92%;
  padding: 13px 15px;
  width: 100%;
}

.library .filter .dates {
  padding: 0 0 20px;
  clear: both;
}

.library .filter .dates input {
  padding: 4px;
}

.filter input {
  background: #f2f2f2;
  border: none;
  border-radius: 4px;
}

input.form-group__keyword {
  background: #f2f2f2;
  border: none;
  border-radius: 4px;
}

.d-flex {
  display: flex;
}

input.form-group__keyword {
  margin: 0;
  width: 75% !important;
}

button.btn-keyword {
  width: 25% !important;
  padding: 10px;
  margin: 0px;
}

button.btn-keyword::after {
  content: none;
}
</style>
